import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { 
  User, 
  Award, 
  Calendar, 
  CreditCard, 
  Download, 
  BookOpen,
  Users,
  LogOut,
  Settings,
  Bell,
  CheckCircle
} from 'lucide-react';

interface MemberProfile {
  id: string;
  full_name: string;
  email: string;
  organization: string;
  position: string;
  phone: string;
  country: string;
  membership_status: string;
  payment_status: string;
  membership_start_date: string;
  membership_end_date: string;
  membership_types_2025_12_03_10_30: {
    name: string;
    price_eur: number;
    benefits: string[];
    discount_percentage: number;
  };
}

const MemberDashboard = () => {
  const [profile, setProfile] = useState<MemberProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const { user, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/member-login');
      } else {
        fetchProfile(user.id);
      }
    }
  }, [user, authLoading, navigate]);

  // Remove the old checkAuth function since we're using useAuth hook now

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from('member_profiles_2025_12_03_10_30')
      .select(`
        *,
        membership_types_2025_12_03_10_30 (
          name,
          price_eur,
          benefits,
          discount_percentage
        )
      `)
      .eq('user_id', userId)
      .single();

    if (error) {
      console.error('Error fetching profile:', error);
      
      // If profile doesn't exist, create a basic one
      if (error.code === 'PGRST116') {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { error: insertError } = await supabase
            .from('member_profiles_2025_12_03_10_30')
            .insert({
              user_id: userId,
              full_name: user.user_metadata?.full_name || '',
              email: user.email || '',
              membership_status: 'pending',
              payment_status: 'unpaid'
            });
          
          if (!insertError) {
            // Retry fetching the profile
            await fetchProfile(userId);
            return;
          }
        }
      }
    } else {
      setProfile(data);
    }
    setProfileLoading(false);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const getMembershipProgress = () => {
    if (!displayProfile?.membership_start_date || !displayProfile?.membership_end_date) return 0;
    
    const start = new Date(displayProfile.membership_start_date);
    const end = new Date(displayProfile.membership_end_date);
    const now = new Date();
    
    const total = end.getTime() - start.getTime();
    const elapsed = now.getTime() - start.getTime();
    
    return Math.max(0, Math.min(100, (elapsed / total) * 100));
  };

  const getDaysRemaining = () => {
    if (!displayProfile?.membership_end_date) return 0;
    
    const end = new Date(displayProfile.membership_end_date);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-600';
      case 'pending': return 'text-orange-600';
      case 'expired': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  if (authLoading || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <User className="h-12 w-12 text-primary mx-auto mb-4" />
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // Show dashboard even without complete profile - user can complete it later
  const displayProfile = profile || {
    id: 'temp',
    full_name: user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Member',
    email: user?.email || '',
    organization: '',
    position: '',
    phone: '',
    country: '',
    membership_status: 'pending',
    payment_status: 'unpaid',
    membership_start_date: '',
    membership_end_date: '',
    membership_types_2025_12_03_10_30: null
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-3">
              <Award className="h-8 w-8 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">CSI Member Portal</h1>
                <p className="text-sm text-muted-foreground">
                  {displayProfile.membership_types_2025_12_03_10_30?.name || 'Member Dashboard'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm">
                <Bell className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm">
                <Settings className="h-4 w-4" />
              </Button>
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">Welcome back, {displayProfile.full_name}!</h2>
          <p className="text-muted-foreground">
            Here's an overview of your CSI membership and available resources.
          </p>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Membership Status</CardTitle>
              <User className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold capitalize ${getStatusColor(displayProfile.membership_status)}`}>
                {displayProfile.membership_status}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Payment Status</CardTitle>
              <CreditCard className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold capitalize ${displayProfile.payment_status === 'paid' ? 'text-green-600' : 'text-red-600'}`}>
                {displayProfile.payment_status}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Days Remaining</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{getDaysRemaining()}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Course Discount</CardTitle>
              <Award className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-primary">
                {displayProfile.membership_types_2025_12_03_10_30?.discount_percentage || 0}%
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Membership Progress */}
        {displayProfile.membership_start_date && displayProfile.membership_end_date && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Membership Period</CardTitle>
              <CardDescription>
                Your membership is valid from {new Date(displayProfile.membership_start_date).toLocaleDateString()} 
                to {new Date(displayProfile.membership_end_date).toLocaleDateString()}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Progress value={getMembershipProgress()} className="w-full" />
              <div className="flex justify-between text-sm text-muted-foreground mt-2">
                <span>Started: {new Date(displayProfile.membership_start_date).toLocaleDateString()}</span>
                <span>Expires: {new Date(displayProfile.membership_end_date).toLocaleDateString()}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="benefits">Benefits</TabsTrigger>
            <TabsTrigger value="resources">Resources</TabsTrigger>
            <TabsTrigger value="profile">Profile</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Your Membership</CardTitle>
                  <CardDescription>
                    {displayProfile.membership_types_2025_12_03_10_30?.name || 'Pending Membership'} - €{displayProfile.membership_types_2025_12_03_10_30?.price_eur || 0}/year
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span>Digital Badge & Certificate</span>
                      <Button size="sm" variant="outline">
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Membership Certificate</span>
                      <Button size="sm" variant="outline">
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Button className="h-20 flex flex-col gap-2">
                      <BookOpen className="h-6 w-6" />
                      <span>Browse Courses</span>
                    </Button>
                    <Button variant="outline" className="h-20 flex flex-col gap-2">
                      <Users className="h-6 w-6" />
                      <span>Join Events</span>
                    </Button>
                    <Button variant="outline" className="h-20 flex flex-col gap-2">
                      <Award className="h-6 w-6" />
                      <span>View Achievements</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="benefits">
            <Card>
              <CardHeader>
                <CardTitle>Your Membership Benefits</CardTitle>
                <CardDescription>
                  All the benefits included with your {displayProfile.membership_types_2025_12_03_10_30?.name || 'CSI'} membership
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {displayProfile.membership_types_2025_12_03_10_30?.benefits?.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>{benefit}</span>
                    </div>
                  )) || (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground">Complete your membership application to see your benefits</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="resources">
            <div className="grid gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Member Resources</CardTitle>
                  <CardDescription>
                    Access exclusive content and materials
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12">
                    <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Resource library coming soon</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>
                  Your personal and membership details
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Full Name</label>
                      <p className="text-sm text-muted-foreground">{displayProfile.full_name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Email</label>
                      <p className="text-sm text-muted-foreground">{displayProfile.email}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Organization</label>
                      <p className="text-sm text-muted-foreground">{displayProfile.organization || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Position</label>
                      <p className="text-sm text-muted-foreground">{displayProfile.position || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Phone</label>
                      <p className="text-sm text-muted-foreground">{displayProfile.phone || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Country</label>
                      <p className="text-sm text-muted-foreground">{displayProfile.country || 'Not specified'}</p>
                    </div>
                  </div>
                  <div className="pt-4">
                    <Button>Edit Profile</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default MemberDashboard;